import { useState } from "react";
import { type GetAllDriversDto } from "../../models/app/admin/driver";
import { type FilterGreyFleetDto } from "../../models/app/admin/driverFilters";
import { type FilterType } from "../../models/app/admin/filter";
import { useCompanyDepartmentSelector } from "../ReusableComponents/CompanyDepartmentSelector";
import { driverListFiltersDefaultValues } from "../DriverListFilters/DriversListFiltersForm/driversListFiltersDefaultValues";
import {
    tableHeaderOptionsEnum,
    useTableHeaders,
} from "../../hooks/useTableHeaders";
import { match } from "ts-pattern";
import { driverVehicleTableAllowedIdsEn } from "../ReusableComponents/SharedTableContent/driverVehicleTableContent";
import useTableStore from "../../stores/useTableStore";
import { driverCategoryOptionsEn } from "../../utils/driverPageLocation";
import { driverTableAllowedIdsEn } from "../DriverList";

export const useGreyFleetAnalysisActions = () => {
    const setAppliedFilter = useTableStore((state) => state.setAppliedFilter);
    const defaultVals = driverListFiltersDefaultValues("greyfleet");
    const [isLoading, setIsLoading] = useState(false);

    const { selectedCompany, selectedDepartment } =
        useCompanyDepartmentSelector();
    const companyDepartmentVals: Pick<GetAllDriversDto, "company"> = {
        company: {
            ...(selectedCompany && { companyIds: [selectedCompany.companyId] }),
            ...(selectedDepartment && {
                departmentIds: [selectedDepartment.departmentId],
            }),
        },
    };

    const { updateTableConfig } = useTableHeaders(
        tableHeaderOptionsEnum.greyFleet
    );
    const setScrollToTable = useTableStore((state) => state.setScrollToTable);

    const handleApplyFilter = async <AllowedIds extends string>(
        filter: FilterType,
        columns?: AllowedIds[]
    ) => {
        setIsLoading(true);
        setAppliedFilter(filter, driverCategoryOptionsEn.greyfleet);

        if (columns) {
            updateTableConfig(columns);
        }

        setScrollToTable(true);
        setIsLoading(false);
    };

    const applyGreyFleetQueryFilter = (
        key: keyof FilterGreyFleetDto,
        value: boolean
    ) => {
        const filter: FilterType = {
            filterId: "",
            filterFields: {
                ...defaultVals,
                ...companyDepartmentVals,
                greyFleetQuery: {
                    ...defaultVals.greyFleetQuery,
                    [key]: value,
                },
            },
        };

        match(key)
            .with("hasMotExpired", "hasExpiringMot", () =>
                handleApplyFilter(filter, [
                    driverVehicleTableAllowedIdsEn.motExpiry,
                ])
            )
            .with("hasExpiringRoadTax", "hasRoadTaxExpired", () =>
                handleApplyFilter(filter, [
                    driverVehicleTableAllowedIdsEn.roadTaxExpiry,
                ])
            )
            .with("hasPolicyWithoutBusinessCover", () =>
                handleApplyFilter(filter, [
                    driverVehicleTableAllowedIdsEn.businessCover,
                ])
            )
            .with(
                "hasExpiringInsurancePolicy",
                "hasInsurancePolicyExpired",
                () =>
                    handleApplyFilter(filter, [
                        driverVehicleTableAllowedIdsEn.vehicleInsuranceExpiry,
                    ])
            )
            .with("hasCompletedInvite", () =>
                handleApplyFilter(filter, [
                    driverTableAllowedIdsEn.GreyFleetInviteEmailLastSent,
                ])
            )
            .otherwise(() => handleApplyFilter(filter));
    };

    return {
        applyGreyFleetQueryFilter,
        isLoading,
    };
};
